import { compareAsc, formatDate } from 'core/utils/date'

import { type BulkRateAvailability } from './BulkRateAvailability'
import { type Quote } from './Quote'
import { type QuoteStop } from './QuoteStop'
import { type Rate } from './Rate'
import { type SortBy } from './SortBy'
import { type SpotQuoteStatus } from './SpotQuoteStatus'

export type BulkRate = {
  availability: BulkRateAvailability
  carrierNotes?: string
  quote: Quote
  status: SpotQuoteStatus
  uuid: string
} & Rate

export type GroupedByDeadlinesBulkRate = {
  deadline: Date
  rates: Array<BulkRate>
}

export type GroupedByLanesBulkRate = {
  pickup: string
  delivery: string
  rates: Array<BulkRate>
}

export function getPickupAndDeliveryTitles(stops: Array<QuoteStop>) {
  const { 0: pickup, [stops.length - 1]: delivery } = stops

  return {
    pickup: `${pickup.city}, ${pickup.state}`,
    delivery: `${delivery.city}, ${delivery.state}`,
  }
}

/**
 * It'll return grouped bulk rates using the quote deadline as keys
 */
export function groupByDeadlines(
  rates: Array<BulkRate>
): Record<string, GroupedByDeadlinesBulkRate> {
  const sortedRates = sortRatesByDeadline(rates)

  return sortedRates.reduce((grouped: Record<string, GroupedByDeadlinesBulkRate>, rate) => {
    const key = formatDate(rate.quote.bidsDeadline, 'yyyy-MM-dd')

    let group: GroupedByDeadlinesBulkRate = grouped[key]

    if (group) {
      group.rates.push(rate)
      return grouped
    }

    group = {
      deadline: rate.quote.bidsDeadline,
      rates: [rate],
    }

    return { ...grouped, [key]: group }
  }, {})
}

/**
 * It'll return grouped bulk rates using pickup and delivery as keys
 */
export function groupByLanes(rates: Array<BulkRate>): Record<string, GroupedByLanesBulkRate> {
  const groupedRates = rates.reduce((grouped: Record<string, GroupedByLanesBulkRate>, rate) => {
    const { pickup, delivery } = getPickupAndDeliveryTitles(rate.quote.stops)

    const key = `${pickup}-${delivery}`

    let group: GroupedByLanesBulkRate = grouped[key]

    if (group) {
      group.rates.push(rate)
      return grouped
    }

    group = {
      pickup,
      delivery,
      rates: [rate],
    }

    return { ...grouped, [key]: group }
  }, {})

  for (const group of Object.values(groupedRates)) {
    group.rates = sortRatesByDeadline(group.rates)
  }

  return groupedRates
}

export function groupBySort(
  rates: Array<BulkRate>,
  sortBy: SortBy
): Record<string, GroupedByDeadlinesBulkRate | GroupedByLanesBulkRate> {
  switch (sortBy) {
    case 'deadline':
      return groupByDeadlines(rates)
    case 'lane':
      return groupByLanes(rates)
    default:
      throw new TypeError('Invalid sort option')
  }
}

export function isSubmitted(rate: BulkRate): boolean {
  return rate.status !== 'pending' && Boolean(rate.flatRate)
}

export function sortRatesByDeadline(rates: Array<BulkRate>): Array<BulkRate> {
  return [...rates].sort((a, b) => compareAsc(a.quote.bidsDeadline, b.quote.bidsDeadline))
}
